/**
 * Form Code Derivation
 *
 * Derives a short, human-readable, uppercase form code from a CRF form name.
 * Used by the CRF Instruction Guide generator and renderer to produce
 * SEER-style chapter headings like "[AE] Adverse Events" instead of the
 * raw Firestore document UUID that's stored as the form's id.
 *
 * Identical logic must exist on the frontend (web/src/utils/docxGenerator.ts)
 * so the AI-generated chapter heading and the form lookup map both produce
 * the same code for the same form name.
 *
 * This is the DEFAULT for a new form's code. Making codes unique across a
 * study — first form keeps its natural code, a later clash gets a letter
 * alternative — is ONE rule in ONE place, `formCodeAssignment.ts`, shared by
 * the page and the server (2026-09-14). The numeric-suffix map builder that
 * used to live here is gone.
 *
 * Examples:
 *   "Adverse Event"                          → "AE"
 *   "Adverse Events"                         → "AE"
 *   "Concomitant Medications"                → "CM"
 *   "Demographics"                           → "DEMO"
 *   "Telephone Follow-Up"                    → "TF"
 *   "Study Drug Administration (Semaglutide)" → "SDA"
 *   "Vital Signs"                            → "VS"
 *   "Eligibility Criteria"                   → "EC"
 *   "DEXA Imaging Results"                   → "DIR"
 *   "Quality of Life Questionnaire"          → "QOLQ"
 *   "Q-LES-Q"                                → "QLESQ"
 */
export function deriveFormCode(name: string | undefined | null): string {
    if (!name) return "FORM";

    // Strip parenthetical clarifications: "Study Drug (X)" → "Study Drug"
    let cleaned = name.replace(/\([^)]*\)/g, " ");

    // Strip trailing "Form" / "Page N" suffixes
    cleaned = cleaned
        .replace(/\bForm\b/gi, " ")
        .replace(/[-–—]\s*Page\s+\d+/gi, " ");

    // Strip non-letter/digit characters except spaces and hyphens
    cleaned = cleaned.replace(/[^A-Za-z0-9\s-]/g, " ");

    // Tokenize on whitespace and hyphens
    const words = cleaned
        .split(/[\s-]+/)
        .map((w) => w.trim())
        .filter((w) => w.length > 0);

    if (words.length === 0) return "FORM";

    // Single-word names: take first 4 chars uppercased (avoids 1-letter codes)
    if (words.length === 1) {
        return words[0].substring(0, 4).toUpperCase();
    }

    // Multi-word names: take the first letter of each significant word.
    // Filter out common stopwords so "Visit Log / Completion" doesn't become "VLC".
    const stopwords = new Set([
        "of", "and", "the", "to", "in", "on", "for", "with", "a", "an",
        "from", "at", "by", "or",
    ]);
    const significant = words.filter((w) => !stopwords.has(w.toLowerCase()));
    const initials = (significant.length > 0 ? significant : words)
        .map((w) => w.charAt(0).toUpperCase())
        .join("");

    // Cap length at 6 characters to keep codes scannable
    return initials.substring(0, 6);
}
